/**
 * Stable per-visitor identifier used as Mixpanel `distinct_id` for the
 * server-side public-pageview tracking path.
 *
 * Stored in localStorage so it persists across pageviews and tabs but
 * resets if the visitor clears site data. Falls back to an ephemeral
 * UUID if localStorage is unavailable (private browsing, locked-down
 * browser, SSR) — better to over-count uniques than to lose the event.
 */
const KEY = "kp_visitor_id";

export function getOrCreateVisitorId(): string {
	if (typeof window === "undefined") {
		// SSR safety. Real consumers always run inside an effect, so this
		// branch is defensive only.
		return crypto.randomUUID();
	}
	try {
		const existing = window.localStorage.getItem(KEY);
		if (existing) {
			return existing;
		}
		const fresh = crypto.randomUUID();
		window.localStorage.setItem(KEY, fresh);
		return fresh;
	} catch {
		return crypto.randomUUID();
	}
}

/**
 * sessionStorage-backed throttle for pageview events. Prevents inflated
 * counts from back-nav, modal-driven remounts, etc. Returns true if the
 * given key was tracked within the last `windowMs` (default 30 min) —
 * the caller should skip emitting in that case.
 *
 * Fails open on storage errors so a locked-down browser still emits
 * events (over-counting beats no data).
 */
const TRACK_PREFIX = "kp_tracked:";
const DEFAULT_WINDOW_MS = 30 * 60 * 1000;
export function alreadyTrackedRecently(
	key: string,
	windowMs: number = DEFAULT_WINDOW_MS,
): boolean {
	if (typeof window === "undefined") {
		return false;
	}
	try {
		const fullKey = `${TRACK_PREFIX}${key}`;
		const last = window.sessionStorage.getItem(fullKey);
		const now = Date.now();
		if (last && now - Number(last) < windowMs) {
			return true;
		}
		window.sessionStorage.setItem(fullKey, String(now));
		return false;
	} catch {
		return false;
	}
}
